import { LoadingOverlay, Modal, Space } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useParams } from 'react-router-dom';
import { SmallModal } from '../../../components/SmallModal';
import { Title } from '../../../components/Title';
import { FormButtons } from '../../../components/form/FormButtons';
import { mutate } from '../../../util/api/mutate';
import { useUpsertRearDisplayForm } from '../hooks/useUpsertRearDisplayForm';
import type { UpsertRearDisplayDto } from '../types/RearDisplayDto';
import { UpsertRearDisplayForm } from './UpsertRearDisplayForm';

interface NewRearDisplayModalProps {
	close: () => void;
	isOpened: boolean;
	triggerListUpdate: () => void;
}

export function NewRearDisplayModal({
	isOpened,
	close,
	triggerListUpdate,
}: NewRearDisplayModalProps) {
	const { organisation } = useParams();

	const [
		showSuccessModal,
		{ open: openSuccessModal, close: closeSuccessModal },
	] = useDisclosure(false);

	const { form, inputs } = useUpsertRearDisplayForm();

	const newRearDisplayMutation = mutate<UpsertRearDisplayDto>(
		`${organisation}/rear-display/add`,
		inputs,
		'multipart/form-data',
	);

	const resetAndClose = () => {
		form.reset();
		close();
		closeSuccessModal();
	};

	const complete = () => {
		triggerListUpdate();
		resetAndClose();
	};

	return (
		<>
			<Modal
				opened={isOpened}
				onClose={resetAndClose}
				title={<Title label={'New Rear Display'} />}
				w={100}
				withCloseButton={true}
				size={'md'}
				centered
				translate="no"
			>
				<LoadingOverlay visible={newRearDisplayMutation.isLoading} />
				<SmallModal
					isOpen={showSuccessModal}
					close={complete}
					title={'Rear Display Added!'}
					body={'A new Rear Display has been added. Click Ok to continue...'}
				/>
				<Space h={6} />
				<form
					onSubmit={form.onSubmit(async () => {
						await newRearDisplayMutation.mutateAsync().then((value) => {
							if (value.isValid) {
								openSuccessModal();
							}
						});
					})}
				>
					<UpsertRearDisplayForm form={form} />
					<FormButtons close={resetAndClose} reducePaddingTop />
				</form>
			</Modal>
		</>
	);
}
